import { useState } from "react";
import { Loader2, Wand2 } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import AILogoOptions from "@/components/ai/AILogoOptions";

// Retouche du logo sélectionné : une consigne libre (ou une suggestion en un
// clic) et, au besoin, des options. Chaque retouche produit une NOUVELLE
// version, ajoutée à la suite des précédentes — rien n'est écrasé, on peut
// revenir à n'importe quelle version et repartir de là.

export const EDIT_SUGGESTIONS = [
  "Plus minimaliste",
  "Couleurs plus vives",
  "Couleurs plus sobres",
  "Retirer le texte",
  "Traits plus épais",
  "Formes plus arrondies",
  "Symbole plus grand",
  "Style plus moderne",
];

export default function AILogoEditor({ versionLabel, options, busy, onEdit }) {
  const [instruction, setInstruction] = useState("");
  const [picked, setPicked] = useState([]);
  const [showOptions, setShowOptions] = useState(false);
  const text = instruction.trim();

  const submit = async () => {
    if (!text || busy) return;
    const ok = await onEdit(text, picked);
    if (ok) setInstruction("");
  };

  return (
    <div className="border border-stone rounded-sm bg-white p-3 space-y-2.5" data-testid="ai-logo-editor">
      <p className="text-[11px] uppercase tracking-[0.15em] text-ash">
        Retoucher {versionLabel ? `la ${versionLabel.toLowerCase()}` : "ce logo"} avec l&apos;IA
      </p>
      <Textarea
        data-testid="ai-logo-edit-instruction"
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
        }}
        rows={2}
        maxLength={500}
        disabled={busy}
        placeholder="Ex : garde le symbole mais utilise un bleu plus foncé et une police plus fine."
        className="rounded-sm border-stone bg-white text-base md:text-sm"
      />
      <div className="flex flex-wrap gap-1.5">
        {EDIT_SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            disabled={busy}
            data-testid={`ai-logo-edit-suggestion-${s}`}
            onClick={() => setInstruction((v) => (v.trim() ? `${v.trim().replace(/[.;,]$/, "")}, ${s.toLowerCase()}` : s))}
            className="text-[11px] px-2.5 py-1 rounded-full border border-stone text-graphite hover:border-copper hover:text-ink transition-colors disabled:opacity-50"
          >
            {s}
          </button>
        ))}
      </div>
      {!!options?.length && (
        <div>
          <button
            type="button"
            onClick={() => setShowOptions((v) => !v)}
            aria-expanded={showOptions}
            className="text-[11px] text-copper hover:text-ink"
          >
            {showOptions ? "Masquer les options" : `Options de la retouche${picked.length ? ` (${picked.length})` : ""}`}
          </button>
          {showOptions && (
            <div className="mt-2">
              <AILogoOptions options={options} value={picked} onChange={setPicked} compact title="Options" testid="ai-logo-edit-options" />
            </div>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] text-ash">La retouche crée une nouvelle version, la précédente est conservée.</p>
        <button
          type="button"
          data-testid="ai-logo-edit-submit"
          disabled={!text || busy}
          onClick={submit}
          className="inline-flex items-center gap-1.5 shrink-0 bg-copper text-cream px-4 py-2 uppercase tracking-[0.12em] text-[11px] hover:bg-ink transition-colors disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
          {busy ? "Retouche en cours…" : "Retoucher"}
        </button>
      </div>
    </div>
  );
}
